import { useState } from 'react';

export interface FlowStep {
  label: string;
  detail: string;
}

interface FlowExplorerProps {
  steps: FlowStep[];
  direction?: 'horizontal' | 'vertical';
  accent?: 'primary' | 'accent' | 'secondary';
  dark?: boolean;
}

const accentMap = {
  primary: {
    active: 'bg-primary-500 text-background-50',
    dot: 'bg-primary-500',
  },
  accent: {
    active: 'bg-accent-500 text-background-50',
    dot: 'bg-accent-500',
  },
  secondary: {
    active: 'bg-secondary-500 text-background-50',
    dot: 'bg-secondary-500',
  },
};

export default function FlowExplorer({
  steps,
  direction = 'horizontal',
  accent = 'primary',
  dark = false,
}: FlowExplorerProps) {
  const [active, setActive] = useState(0);
  const current = steps[active];
  const ac = accentMap[accent];

  const nodeBase = `px-4 py-2.5 rounded-md text-sm whitespace-nowrap cursor-pointer transition-all border ${
    dark ? 'border-background-700 bg-foreground-900 text-foreground-300' : 'border-background-200 bg-background-50 text-foreground-700'
  }`;

  return (
    <div className="w-full">
      {direction === 'horizontal' ? (
        <div className="flex flex-wrap items-center gap-2">
          {steps.map((s, i) => (
            <div key={s.label} className="flex items-center gap-2">
              <button
                onClick={() => setActive(i)}
                className={`${nodeBase} ${
                  i === active
                    ? `${ac.active} border-transparent font-semibold`
                    : 'hover:border-primary-300'
                }`}
              >
                {s.label}
              </button>
              {i < steps.length - 1 && (
                <i
                  className={`ri-arrow-right-line text-sm ${
                    dark ? 'text-foreground-600' : 'text-foreground-400'
                  }`}
                ></i>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {steps.map((s, i) => (
            <button
              key={s.label}
              onClick={() => setActive(i)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-md text-left text-sm cursor-pointer transition-all border ${
                i === active
                  ? `${ac.active} border-transparent font-semibold`
                  : `${nodeBase} hover:border-primary-300`
              }`}
            >
              <span
                className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  i === active ? 'bg-background-50' : ac.dot
                }`}
              ></span>
              {s.label}
            </button>
          ))}
        </div>
      )}

      <div
        className={`mt-6 p-6 rounded-lg border ${
          dark
            ? 'bg-foreground-900 border-foreground-700'
            : 'bg-background-100 border-background-200'
        }`}
      >
        <div
          className={`flex items-center gap-2 font-heading font-bold ${
            dark ? 'text-background-50' : 'text-foreground-950'
          }`}
        >
          <span className={`w-6 h-6 rounded-full ${ac.active} flex items-center justify-center text-xs`}>
            {active + 1}
          </span>
          {current.label}
        </div>
        <p
          className={`mt-3 text-sm leading-relaxed ${
            dark ? 'text-foreground-300' : 'text-foreground-600'
          }`}
        >
          {current.detail}
        </p>
      </div>
    </div>
  );
}